import { FC } from 'react';
import { PortableText } from '@portabletext/react';
import type { PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';

type AccentHeadlineProps = {
  /** Portable Text headline; spans marked "accent" render in the accent colour. */
  value?: PortableTextBlock[] | null;
  /** Plain-string fallback for headings not yet migrated to Portable Text. */
  fallback?: string;
  className?: string;
  /** Forwarded to the heading element (e.g. for test hooks). */
  dataCy?: string;
};

const accentComponents: PortableTextComponents = {
  marks: {
    // The custom "accent" decorator (defined on the accentHeadline type in
    // sanity/schemaTypes/quizBlocks/gytrStepObjects.ts) tints a run of words
    // in the funnel accent colour, e.g. "30 seconds".
    accent: ({ children }) => (
      <span className="text-primary-500">{children}</span>
    ),
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>
  },
  block: {
    // Render each block inline-ish: the wrapping element owns the heading
    // styling, so a block is just its text plus a space between blocks.
    normal: ({ children }) => <>{children} </>
  }
};

/**
 * Renders a step headline that may colour individual words via the "accent"
 * Portable Text mark. Falls back to a plain string when no rich value is set.
 */
const AccentHeadline: FC<AccentHeadlineProps> = ({
  value,
  fallback,
  className,
  dataCy
}) => {
  if (value && value.length > 0) {
    return (
      <h1 className={className} data-cy={dataCy}>
        <PortableText value={value} components={accentComponents} />
      </h1>
    );
  }

  if (!fallback) return null;

  return (
    <h1 className={className} data-cy={dataCy}>
      {fallback}
    </h1>
  );
};

export default AccentHeadline;
